/**
 * CanonicalReferenceData.tsx  (v3 — deduplicated reference domains)
 * =======================================================================
 *
 * Layout
 * ------
 * ┌─────────────┬──────────────────────────────────────────────────────────┐
 * │ Domain list │  Right panel                                             │
 * │             │  ┌─ Domain header: label · codes · raw variants ─────── │
 * │ [Currency   │  │  + "Used by N canonical fields" chip strip            │
 * │  Code]      │  └────────────────────────────────────────────────────── │
 * │ [Country    │  ┌─ Tab bar ─────────────────────────────────────────── │
 * │  Code ×4]  │  │  [Canonical Values] [Source Crosswalk] [DM Lineage]  │
 * │ ...         │  └────────────────────────────────────────────────────── │
 * └─────────────┴──────────────────────────────────────────────────────────┘
 *
 * Three tabs
 * ----------
 * 1. Canonical Values   — editable table of reference codes
 * 2. Source Crosswalk   — per code: which source fields & raw variants map to it
 *                         grouped by canonical field (since shared domains have
 *                         multiple canonical fields contributing)
 *                         Remap control on each entry
 * 3. DM Lineage         — visual: reference domain → canonical DM fields → source fields
 *                         Shows the full chain end-to-end
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles, faTags, faLayerGroup, faExchangeAlt,
  faCircleXmark, faPlus, faPen, faFloppyDisk, faXmark,
  faRotateRight, faChevronRight, faChevronDown, faCircleInfo,
  faTriangleExclamation, faFilter, faSitemap, faLink,
  faShareNodes, faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { T, cls, injectTheme } from '../theme/theme';
import {
  adriva_canonical_buildTokenMap,
  adriva_canonical_getTokenMap,
  adriva_canonical_patchCanonicalValue,
  adriva_canonical_addCanonicalValue,
  adriva_canonical_deactivateCanonicalValue,
  adriva_canonical_remapCrosswalk,
} from '../API';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CanonicalValue {
  value: string; label: string; description: string;
  is_active: boolean; sort_order: number; sme_note: string;
  source: 'derived' | 'sme_added';
}

interface CrosswalkEntry {
  entity_id: string; source_prefix: string; column: string; rule_id: string;
  lineage_fld_id?: string; lineage_confidence?: number;
  used_by_canonical_field: string;
  raw_values: string[]; sme_remap: string | null;
}

interface UsedByField {
  canon_col: string; canon_label: string;
  dtype: string; max_lineage_confidence: number;
}

interface ReferenceDomain {
  domain_key: string; domain_label: string; token_type: string;
  canonical_values: CanonicalValue[];
  used_by_canonical_fields: UsedByField[];
  crosswalk: Record<string, CrosswalkEntry[]>;
}

interface CanonicalTokenMap {
  canonical_entity_id: string; source_entity_ids: string[];
  generated_at: string; version: number;
  reference_domains: Record<string, ReferenceDomain>;
}

interface Props {
  canonicalEntityId: string;
  appNm: string;
}

// ─── Colour helpers ───────────────────────────────────────────────────────────

const PALETTE = [
  { color: T.brand,  bg: T.brandLight, border: T.brandMid      },
  { color: T.green,  bg: T.greenBg,    border: T.greenBorder    },
  { color: T.amber,  bg: T.amberBg,    border: T.amberBorder    },
  { color: T.teal,   bg: T.tealBg,     border: T.tealBorder     },
  { color: T.purple, bg: T.purpleBg,   border: T.purpleBorder   },
  { color: T.blue,   bg: T.blueBg,     border: T.blueBorder     },
  { color: T.red,    bg: T.redBg,      border: T.redBorder      },
];

function domainColor(idx: number) {
  const i = ((idx % PALETTE.length) + PALETTE.length) % PALETTE.length;
  return PALETTE[i];
}

function sysStyle(prefix: string) {
  const u = (prefix || '').toUpperCase();
  if (u.includes('SAP'))    return { bg: T.blueBg,  text: T.blue  };
  if (u.includes('ORACLE')) return { bg: T.amberBg, text: T.amber };
  return                           { bg: T.slateBg, text: T.slate };
}

function dtypeBadge(dtype: string) {
  if (dtype === 'LookupField')  return { label: 'Lookup',  bg: T.greenBg,  text: T.green  };
  if (dtype === 'TextField')    return { label: 'Text',    bg: T.blueBg,   text: T.blue   };
  if (dtype === 'BooleanField') return { label: 'Boolean', bg: T.amberBg,  text: T.amber  };
  return                               { label: dtype,     bg: T.slateBg,  text: T.slate  };
}

function confBar(conf: number, color: string) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 60 }}>
      <div style={{ flex: 1, height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.round(conf * 100)}%`, height: '100%',
          background: conf >= 0.9 ? T.green : conf >= 0.7 ? T.amber : T.red,
          borderRadius: 2,
        }} />
      </div>
      <span style={{ fontSize: 9, color, fontFamily: "'JetBrains Mono', monospace", minWidth: 28 }}>
        {Math.round(conf * 100)}%
      </span>
    </div>
  );
}

// ─── Domain sidebar item ──────────────────────────────────────────────────────

function DomainItem({ domain, index, active, onClick }: {
  domain: ReferenceDomain; index: number; active: boolean; onClick: () => void;
}) {
  const { color, bg, border } = domainColor(index);
  const usedBy   = (domain.used_by_canonical_fields || []).length;
  const active_cv = (domain.canonical_values || []).filter(cv => cv.is_active).length;
  const totalRaw = Object.values(domain.crosswalk || {})
    .flatMap(e => e).reduce((s, e) => s + (e.raw_values?.length || 0), 0);
  const remapped = Object.values(domain.crosswalk || {})
    .flatMap(e => e).filter(e => e.sme_remap).length;

  return (
    <button onClick={onClick} style={{
      width: '100%', textAlign: 'left', padding: '9px 14px',
      background: active ? bg : 'transparent', border: 'none',
      borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
      cursor: 'pointer', transition: 'all .12s',
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.bgSidebar; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: active ? color : T.textPrimary, marginBottom: 3 }}>
        {domain.domain_label}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
          padding: '1px 6px', borderRadius: 8, background: bg, color, border: `1px solid ${border}`,
        }}>{active_cv} codes</span>
        {usedBy > 1 && (
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
            padding: '1px 6px', borderRadius: 8,
            background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}`,
          }}>
            ×{usedBy} fields
          </span>
        )}
        <span style={{ fontSize: 9, color: T.textMuted }}>{totalRaw} raw</span>
        {remapped > 0 && (
          <span style={{ fontSize: 9, color: T.purple }}>✎{remapped}</span>
        )}
      </div>
    </button>
  );
}

// ─── Used-by strip ────────────────────────────────────────────────────────────

function UsedByStrip({ usedBy, filterField, onFilterField }: {
  usedBy: UsedByField[];
  filterField: string | null;
  onFilterField: (f: string | null) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, color: T.textMuted, flexShrink: 0 }}>Used by:</span>
      {usedBy.map(ub => {
        const dt = dtypeBadge(ub.dtype);
        const active = filterField === ub.canon_col;
        return (
          <button
            key={ub.canon_col}
            onClick={() => onFilterField(active ? null : ub.canon_col)}
            title={`Filter crosswalk to ${ub.canon_label}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '3px 10px', borderRadius: 12,
              background: active ? T.brand : T.bgCard,
              color: active ? '#fff' : T.textPrimary,
              border: `1px solid ${active ? T.brand : T.border}`,
              cursor: 'pointer', fontSize: 11, fontWeight: 600,
              transition: 'all .12s',
            }}
          >
            <span style={{
              fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 4,
              background: active ? 'rgba(255,255,255,0.25)' : dt.bg,
              color: active ? '#fff' : dt.text,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {dt.label}
            </span>
            {ub.canon_label}
            {confBar(ub.max_lineage_confidence, active ? '#fff' : T.textMuted)}
          </button>
        );
      })}
      {filterField && (
        <button onClick={() => onFilterField(null)} style={{
          fontSize: 10, color: T.textMuted, border: 'none', background: 'none',
          cursor: 'pointer', padding: '2px 6px',
        }}>
          Clear filter
        </button>
      )}
    </div>
  );
}

// ─── Tab 1: Canonical Values ──────────────────────────────────────────────────

function CanonicalValuesTab({ domain, colorIdx, canonicalEntityId, onRefresh }: {
  domain: ReferenceDomain; colorIdx: number;
  canonicalEntityId: string; onRefresh: () => void;
}) {
  const { color, bg, border } = domainColor(colorIdx);
  const [editVal, setEditVal]   = useState<string | null>(null);
  const [draft,   setDraft]     = useState<Partial<CanonicalValue>>({});
  const [saving,  setSaving]    = useState(false);
  const [addMode, setAddMode]   = useState(false);
  const [newVal,  setNewVal]    = useState({ value: '', label: '', description: '' });
  const [activeOnly, setActiveOnly] = useState(false);

  function rawCount(v: string) {
    return ((domain.crosswalk || {})[v] || []).reduce((s, e) => s + (e.raw_values?.length || 0), 0);
  }
  function usedByCount(v: string) {
    return new Set(((domain.crosswalk || {})[v] || []).map((e: any) => e.used_by_canonical_field)).size;
  }

  const shown = (activeOnly
    ? (domain.canonical_values || []).filter(cv => cv.is_active)
    : (domain.canonical_values || [])
  ).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  async function saveEdit(value: string) {
    setSaving(true);
    try {
      await adriva_canonical_patchCanonicalValue(canonicalEntityId, domain.domain_key, value, draft);
      setEditVal(null); setDraft({}); onRefresh();
    } finally { setSaving(false); }
  }

  async function deactivate(value: string) {
    if (!window.confirm(`Deactivate "${value}"?`)) return;
    await adriva_canonical_deactivateCanonicalValue(canonicalEntityId, domain.domain_key, value);
    onRefresh();
  }

  async function handleAdd() {
    if (!newVal.value.trim()) return;
    await adriva_canonical_addCanonicalValue(canonicalEntityId, domain.domain_key, {
      value: newVal.value.trim(),
      label: newVal.label.trim() || newVal.value.trim(),
      description: newVal.description.trim(),
    });
    setNewVal({ value: '', label: '', description: '' });
    setAddMode(false); onRefresh();
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {(domain.canonical_values||[]).length} values · {(domain.canonical_values||[]).filter(cv => cv.is_active).length} active
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={() => setActiveOnly(f => !f)}
            className={cls('gw-btn gw-btn-sm', activeOnly ? 'gw-btn-primary' : 'gw-btn-ghost')}>
            <FontAwesomeIcon icon={faFilter} /> Active only
          </button>
          <button onClick={() => setAddMode(a => !a)} className="gw-btn gw-btn-sm gw-btn-outline">
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>

      {addMode && (
        <div style={{
          border: `1px dashed ${color}`, borderRadius: 8,
          padding: 12, background: bg, marginBottom: 12,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 8, marginBottom: 8 }}>
            {[['value','Code (value)','e.g. AU'],['label','Label','Australia'],['description','Description','Optional']].map(
              ([key,label,ph]) => (
              <div key={key}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
                  color: T.textMuted, marginBottom: 3, fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
                <input value={(newVal as any)[key]} onChange={e => setNewVal({...newVal,[key]:e.target.value})}
                  placeholder={ph} className="gw-input" style={{ fontSize: 12 }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button onClick={() => setAddMode(false)} className="gw-btn gw-btn-sm gw-btn-ghost">Cancel</button>
            <button onClick={handleAdd} disabled={!newVal.value.trim()}
              className="gw-btn gw-btn-sm gw-btn-primary">
              <FontAwesomeIcon icon={faPlus} /> Add value
            </button>
          </div>
        </div>
      )}

      <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: T.bgSidebar, borderBottom: `1px solid ${T.border}` }}>
              {['Code','Label','Description','Raw','Used by fields','Status','Note',''].map(h => (
                <th key={h} style={{
                  padding: '8px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.1em', color: T.textMuted,
                  fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((cv, i) => {
              const isEdit = editVal === cv.value;
              const raw = rawCount(cv.value);
              const ubCount = usedByCount(cv.value);
              return (
                <tr key={cv.value} style={{
                  background: i % 2 === 0 ? T.bgCard : T.bgSidebar,
                  opacity: cv.is_active ? 1 : 0.45,
                  borderBottom: `1px solid ${T.border}`,
                }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
                      color, background: bg, border: `1px solid ${border}`,
                      padding: '2px 8px', borderRadius: 8,
                    }}>{cv.value}</span>
                    {cv.source === 'sme_added' && (
                      <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700,
                        padding: '1px 5px', borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                        background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}` }}>
                        SME
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', minWidth: 100 }}>
                    {isEdit ? (
                      <input value={draft.label ?? cv.label}
                        onChange={e => setDraft({...draft, label: e.target.value})}
                        className="gw-input" style={{ fontSize: 12 }} />
                    ) : cv.label}
                  </td>
                  <td style={{ padding: '8px 10px', maxWidth: 180 }}>
                    {isEdit ? (
                      <input value={draft.description ?? cv.description}
                        onChange={e => setDraft({...draft, description: e.target.value})}
                        className="gw-input" style={{ fontSize: 11 }} placeholder="Optional…" />
                    ) : <span style={{ fontSize: 11, color: T.textMuted }}>{cv.description || '—'}</span>}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
                      fontWeight: 700, color: raw > 0 ? color : T.textMuted }}>{raw}</span>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    {ubCount > 0 && (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                        fontWeight: 700, color: T.purple }}>{ubCount}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {cv.is_active
                      ? <span className="gw-badge gw-badge-green">Active</span>
                      : <span className="gw-badge gw-badge-muted">Inactive</span>}
                  </td>
                  <td style={{ padding: '8px 10px', maxWidth: 140 }}>
                    {isEdit ? (
                      <input value={draft.sme_note ?? cv.sme_note}
                        onChange={e => setDraft({...draft, sme_note: e.target.value})}
                        className="gw-input" style={{ fontSize: 11 }} placeholder="Note…" />
                    ) : <span style={{ fontSize: 11, color: T.textMuted, fontStyle: 'italic' }}>
                      {cv.sme_note || '—'}
                    </span>}
                  </td>
                  <td style={{ padding: '8px 8px' }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {isEdit ? (
                        <>
                          <button onClick={() => saveEdit(cv.value)} disabled={saving}
                            className="gw-btn gw-btn-sm gw-btn-success">
                            <FontAwesomeIcon icon={faFloppyDisk} style={{ fontSize: 10 }} />
                          </button>
                          <button onClick={() => { setEditVal(null); setDraft({}); }}
                            className="gw-btn gw-btn-sm gw-btn-ghost">
                            <FontAwesomeIcon icon={faXmark} style={{ fontSize: 10 }} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setEditVal(cv.value); setDraft({}); }}
                            className="gw-btn gw-btn-sm gw-btn-ghost">
                            <FontAwesomeIcon icon={faPen} style={{ fontSize: 9 }} />
                          </button>
                          {cv.is_active && (
                            <button onClick={() => deactivate(cv.value)}
                              className="gw-btn gw-btn-sm gw-btn-ghost">
                              <FontAwesomeIcon icon={faCircleXmark} style={{ fontSize: 10, color: T.red }} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {shown.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No values.</div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 2: Source Crosswalk ──────────────────────────────────────────────────

function CrosswalkTab({ domain, colorIdx, canonicalEntityId, filterField, onRefresh }: {
  domain: ReferenceDomain; colorIdx: number;
  canonicalEntityId: string; filterField: string | null; onRefresh: () => void;
}) {
  const { color, bg, border } = domainColor(colorIdx);
  const [expanded,   setExpanded]   = useState<string | null>(null);
  const [remapDraft, setRemapDraft] = useState<Record<string, string>>({});
  const [saving,     setSaving]     = useState<string | null>(null);
  const [search,     setSearch]     = useState('');

  const allValues = (domain.canonical_values || []).map(cv => cv.value);

  const buckets = useMemo(() => {
    const q = search.toLowerCase().trim();
    return Object.entries(domain.crosswalk || {})
      .filter(([cv, entries]) => {
        if (!entries.length) return false;
        const filtered = filterField ? entries.filter(e => e.used_by_canonical_field === filterField) : entries;
        if (!filtered.length) return false;
        if (!q) return true;
        return cv.toLowerCase().includes(q) ||
          filtered.some(e => e.source_prefix.toLowerCase().includes(q) ||
            e.column.toLowerCase().includes(q) ||
            e.raw_values.some(r => r.toLowerCase().includes(q)) ||
            e.used_by_canonical_field.toLowerCase().includes(q));
      })
      .sort(([a], [b]) => a.localeCompare(b));
  }, [domain.crosswalk, filterField, search]);

  async function applyRemap(entry: CrosswalkEntry, currentCv: string) {
    const key = `${entry.entity_id}|${entry.source_prefix}|${entry.column}|${entry.rule_id}|${entry.used_by_canonical_field}`;
    const target = remapDraft[key];
    if (!target || target === currentCv) return;
    setSaving(key);
    try {
      await adriva_canonical_remapCrosswalk(
        canonicalEntityId, domain.domain_key, currentCv, target,
        { entity_id: entry.entity_id, source_prefix: entry.source_prefix,
          column: entry.column, rule_id: entry.rule_id,
          used_by_canonical_field: entry.used_by_canonical_field },
      );
      setRemapDraft(d => { const n = {...d}; delete n[key]; return n; });
      onRefresh();
    } finally { setSaving(null); }
  }

  return (
    <div>
      <div className="gw-notice gw-notice-info" style={{ marginBottom: 10 }}>
        <FontAwesomeIcon icon={faCircleInfo} />
        Each code shows which source fields map to it, grouped by canonical DM field.
        Use the canonical field filter chips above to focus on one DM field at a time.
        <strong> Remap →</strong> corrects mis-mapped entries (source DQ rules unchanged).
      </div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <FontAwesomeIcon icon={faFilter} style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 10, color: T.textMuted, pointerEvents: 'none',
        }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Filter by code, source field, or raw variant…"
          className="gw-input" style={{ paddingLeft: 28, fontSize: 12 }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {buckets.map(([cv, allEntries]) => {
          const entries = filterField
            ? allEntries.filter(e => e.used_by_canonical_field === filterField)
            : allEntries;
          const isOpen  = expanded === cv;
          const totalRaw = entries.reduce((s, e) => s + (e.raw_values?.length || 0), 0);
          const remapped = entries.filter(e => e.sme_remap).length;
          const cvLabel  = (domain.canonical_values||[]).find(c => c.value === cv)?.label;

          // Group entries by canonical field for display
          const byCanonField: Record<string, CrosswalkEntry[]> = {};
          for (const entry of entries) {
            const cf = entry.used_by_canonical_field;
            byCanonField[cf] = byCanonField[cf] || [];
            byCanonField[cf].push(entry);
          }

          return (
            <div key={cv} style={{
              border: `1px solid ${isOpen ? color : T.border}`,
              borderRadius: 10, overflow: 'hidden', background: T.bgCard,
              transition: 'border-color .15s',
            }}>
              {/* Bucket header */}
              <button onClick={() => setExpanded(isOpen ? null : cv)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px',
                background: isOpen ? bg : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                borderBottom: isOpen ? `1px solid ${T.border}` : 'none',
              }}>
                <FontAwesomeIcon icon={faChevronRight} style={{
                  fontSize: 10, color: T.textMuted, flexShrink: 0,
                  transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none',
                }} />
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700,
                  color, background: bg, border: `1px solid ${border}`,
                  padding: '3px 10px', borderRadius: 8, flexShrink: 0,
                }}>{cv}</span>
                {cvLabel && cvLabel !== cv && (
                  <span style={{ fontSize: 11, color: T.textMuted, fontStyle: 'italic' }}>{cvLabel}</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                  {/* Canonical field pills */}
                  <div style={{ display: 'flex', gap: 4 }}>
                    {Object.keys(byCanonField).map(cf => {
                      const ub = (domain.used_by_canonical_fields||[]).find(u => u.canon_col === cf);
                      const dt = dtypeBadge(ub?.dtype || '');
                      return (
                        <span key={cf} style={{
                          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                          background: dt.bg, color: dt.text,
                          fontFamily: "'JetBrains Mono', monospace", border: `1px solid ${T.border}`,
                        }} title={ub?.canon_label}>
                          {ub?.canon_label || cf}
                        </span>
                      );
                    })}
                  </div>
                  <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                    {entries.length} src · {totalRaw} raw
                  </span>
                  {remapped > 0 && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                      background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}`,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{remapped} remapped</span>
                  )}
                </div>
              </button>

              {/* Entries grouped by canonical field */}
              {isOpen && (
                <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Object.entries(byCanonField).map(([cf, cfEntries]) => {
                    const ub = (domain.used_by_canonical_fields||[]).find(u => u.canon_col === cf);
                    const dt = dtypeBadge(ub?.dtype || '');
                    return (
                      <div key={cf}>
                        {/* Canonical field sub-header */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '5px 0 6px', marginBottom: 5,
                          borderBottom: `1px solid ${T.border}`,
                        }}>
                          <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 10, color: T.brand }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: T.brand }}>
                            {ub?.canon_label || cf}
                          </span>
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                            background: dt.bg, color: dt.text,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>{dt.label}</span>
                          {ub && confBar(ub.max_lineage_confidence, T.textMuted)}
                          <span style={{ fontSize: 9, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace", marginLeft: 'auto' }}>
                            {cf}
                          </span>
                        </div>

                        {/* Source entries for this canonical field */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {cfEntries.map((entry, ei) => {
                            const entryKey = `${entry.entity_id}|${entry.source_prefix}|${entry.column}|${entry.rule_id}|${entry.used_by_canonical_field}`;
                            const sys = sysStyle(entry.source_prefix);
                            const draftRemap = remapDraft[entryKey] || '';
                            const isSaving = saving === entryKey;

                            return (
                              <div key={ei} style={{
                                border: `1px solid ${entry.sme_remap ? T.purpleBorder : T.border}`,
                                borderRadius: 8,
                                background: entry.sme_remap ? T.purpleBg : T.bgSidebar,
                                padding: '7px 11px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
                                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                    <span style={{
                                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700,
                                      padding: '2px 7px', borderRadius: 4, background: sys.bg, color: sys.text,
                                    }}>{entry.source_prefix}</span>
                                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: T.textPrimary }}>
                                      {entry.column}
                                    </span>
                                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: T.textMuted }}>
                                      [{entry.entity_id}]
                                    </span>
                                    {entry.lineage_confidence && (
                                      <span style={{ fontSize: 9, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                                        {Math.round(entry.lineage_confidence * 100)}% conf
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 5, alignItems: 'center' }}>
                                    <select value={draftRemap}
                                      onChange={e => setRemapDraft({...remapDraft, [entryKey]: e.target.value})}
                                      className="gw-select"
                                      style={{ fontSize: 11, height: 26, padding: '0 22px 0 6px', minWidth: 100 }}>
                                      <option value="">Remap →</option>
                                      {allValues.filter(v => v !== cv).map(v => (
                                        <option key={v} value={v}>{v}</option>
                                      ))}
                                    </select>
                                    {draftRemap && draftRemap !== cv && (
                                      <button onClick={() => applyRemap(entry, cv)} disabled={isSaving}
                                        className="gw-btn gw-btn-sm gw-btn-primary">
                                        <FontAwesomeIcon icon={faExchangeAlt} />
                                        {isSaving ? '…' : 'Apply'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                  {entry.raw_values.map(rv => (
                                    <span key={rv} style={{
                                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                                      padding: '1px 7px', borderRadius: 12,
                                      background: T.bgCard, color: T.textPrimary, border: `1px solid ${T.border}`,
                                    }}>{rv}</span>
                                  ))}
                                  <span style={{ fontSize: 10, color: T.textMuted }}>
                                    <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: 4, fontSize: 8 }} />
                                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
                                      color: entry.sme_remap ? T.purple : color }}>
                                      {entry.sme_remap ?? cv}
                                    </span>
                                    {entry.sme_remap && (
                                      <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 700,
                                        padding: '1px 5px', borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                                        background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}` }}>
                                        remapped
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {buckets.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
            No entries match the filter.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 3: DM Lineage ────────────────────────────────────────────────────────

function DmLineageTab({ domain, colorIdx }: {
  domain: ReferenceDomain; colorIdx: number;
}) {
  const { color, bg, border } = domainColor(colorIdx);

  // Build: per canonical field → source columns that feed it
  const fieldSourceMap: Record<string, { source_prefix: string; column: string; entity_id: string; raw_count: number; conf?: number }[]> = {};

  for (const [cv, entries] of Object.entries(domain.crosswalk || {})) {
    for (const e of entries) {
      const cf = e.used_by_canonical_field;
      fieldSourceMap[cf] = fieldSourceMap[cf] || [];
      const existing = fieldSourceMap[cf].find(
        s => s.source_prefix === e.source_prefix && s.column === e.column && s.entity_id === e.entity_id
      );
      if (existing) {
        existing.raw_count += e.raw_values.length;
      } else {
        fieldSourceMap[cf].push({
          source_prefix: e.source_prefix,
          column: e.column,
          entity_id: e.entity_id,
          raw_count: e.raw_values.length,
          conf: e.lineage_confidence,
        });
      }
    }
  }

  return (
    <div>
      <div className="gw-notice gw-notice-info" style={{ marginBottom: 14 }}>
        <FontAwesomeIcon icon={faCircleInfo} />
        This reference domain is shared by {(domain.used_by_canonical_fields||[]).length} canonical
        DM field{(domain.used_by_canonical_fields||[]).length !== 1 ? 's' : ''}.
        Each field contributes source columns whose raw values standardise to the same code set.
      </div>

      {/* Flow diagram: Reference Domain → Canonical Fields → Source Columns */}
      <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>

        {/* Column 1: Reference domain */}
        <div style={{ flexShrink: 0, width: 180 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
            color: T.textMuted, marginBottom: 8, fontFamily: "'JetBrains Mono', monospace",
            textAlign: 'center' }}>
            Reference Domain
          </div>
          <div style={{
            border: `2px solid ${color}`, borderRadius: 10, padding: '12px 14px',
            background: bg, textAlign: 'center',
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 4 }}>
              {domain.domain_label}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: T.textMuted, marginBottom: 6 }}>
              {domain.domain_key}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
              {(domain.canonical_values||[]).filter(cv => cv.is_active).slice(0, 6).map(cv => (
                <span key={cv.value} style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
                  padding: '1px 6px', borderRadius: 6, background: color,
                  color: '#fff',
                }}>{cv.value}</span>
              ))}
              {(domain.canonical_values||[]).filter(cv => cv.is_active).length > 6 && (
                <span style={{ fontSize: 9, color: T.textMuted }}>
                  +{(domain.canonical_values||[]).filter(cv => cv.is_active).length - 6}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', marginTop: 40 }}>
          <div style={{ height: 2, width: 20, background: T.border }} />
          <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12, color: T.textMuted }} />
        </div>

        {/* Column 2: Canonical DM fields */}
        <div style={{ flexShrink: 0, minWidth: 200 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
            color: T.textMuted, marginBottom: 8, fontFamily: "'JetBrains Mono', monospace",
            textAlign: 'center' }}>
            Canonical DM Fields
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(domain.used_by_canonical_fields||[]).map((ub, i) => {
              const dt = dtypeBadge(ub.dtype);
              return (
                <div key={ub.canon_col} style={{
                  border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px',
                  background: T.bgCard,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                      background: dt.bg, color: dt.text,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{dt.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.brand }}>{ub.canon_label}</span>
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: T.textMuted, marginBottom: 4 }}>
                    {ub.canon_col}
                  </div>
                  {confBar(ub.max_lineage_confidence, T.textMuted)}
                </div>
              );
            })}
          </div>
        </div>

        {/* Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', marginTop: 40 }}>
          <div style={{ height: 2, width: 20, background: T.border }} />
          <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12, color: T.textMuted }} />
        </div>

        {/* Column 3: Source fields per canonical field */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
            color: T.textMuted, marginBottom: 8, fontFamily: "'JetBrains Mono', monospace",
            textAlign: 'center' }}>
            Source Columns
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(domain.used_by_canonical_fields||[]).map((ub) => {
              const sources = fieldSourceMap[ub.canon_col] || [];
              return (
                <div key={ub.canon_col} style={{
                  border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px',
                  background: T.bgSidebar, minHeight: 44,
                }}>
                  {sources.map((src, si) => {
                    const sys = sysStyle(src.source_prefix);
                    return (
                      <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: si < sources.length - 1 ? 4 : 0 }}>
                        <span style={{
                          fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
                          padding: '1px 5px', borderRadius: 3,
                          background: sys.bg, color: sys.text, flexShrink: 0,
                        }}>{src.source_prefix}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                          fontWeight: 700, color: T.textPrimary }}>
                          {src.column}
                        </span>
                        <span style={{ fontSize: 9, color: T.textMuted }}>[{src.entity_id}]</span>
                        <span style={{ marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 9, color: T.textMuted }}>{src.raw_count} raw</span>
                      </div>
                    );
                  })}
                  {sources.length === 0 && (
                    <span style={{ fontSize: 10, color: T.textMuted }}>No source coverage</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const CanonicalReferenceData: React.FC<Props> = ({ canonicalEntityId, appNm }) => {
  useEffect(() => { injectTheme(); }, []);

  const [tokenMap,    setTokenMap]    = useState<CanonicalTokenMap | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [building,    setBuilding]    = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [activeTab,   setActiveTab]   = useState<'values' | 'crosswalk' | 'lineage'>('values');
  const [filterField, setFilterField] = useState<string | null>(null);
  const [sideSearch,  setSideSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adriva_canonical_getTokenMap(canonicalEntityId);
      if (res?.success && res.data) {
        const data = res.data;
        // v2 compat: rename 'domains' to 'reference_domains'
        if (!data.reference_domains && (data as any).domains) {
          data.reference_domains = (data as any).domains;
        }
        if (!data.reference_domains || Object.keys(data.reference_domains).length === 0) {
          setError('No reference domains found. Click "Build" to generate.');
          setLoading(false);
          return;
        }
        setTokenMap(data);
        if (!selectedDomain) {
          const first = Object.keys(data.reference_domains)[0];
          if (first) setSelectedDomain(first);
        }
      } else {
        setError('Not built yet. Click "Build" to generate.');
      }
    } catch { setError('Not built yet. Click "Build" to generate.'); }
    finally { setLoading(false); }
  }, [canonicalEntityId]);

  useEffect(() => { load(); }, [load]);

  async function handleBuild() {
    setBuilding(true); setError(null);
    try {
      const res = await adriva_canonical_buildTokenMap(canonicalEntityId, appNm);
      if (res?.success) { await load(); }
      else { setError(res?.msg || 'Build failed'); }
    } catch (e: any) { setError(e?.message || 'Build failed'); }
    finally { setBuilding(false); }
  }

  const domainList = useMemo(() => {
    if (!tokenMap) return [];
    const q = sideSearch.toLowerCase().trim();
    const rd = tokenMap.reference_domains || (tokenMap as any).domains || {};
    return (Object.values(rd) as ReferenceDomain[])
      .filter(d => !q || (d.domain_label||'').toLowerCase().includes(q) || (d.domain_key||'').toLowerCase().includes(q))
      .sort((a, b) => (b.canonical_values?.length||0) - (a.canonical_values?.length||0));
  }, [tokenMap, sideSearch]);

  const activeDomain = (tokenMap && selectedDomain)
    ? ((tokenMap.reference_domains || (tokenMap as any).domains || {})[selectedDomain] ?? null) : null;

  const activeColorIdx = useMemo(() => {
    if (!activeDomain) return 0;
    const idx = domainList.findIndex(d => d.domain_key === selectedDomain);
    return idx >= 0 ? idx : 0;
  }, [activeDomain, domainList, selectedDomain]);

  const stats = useMemo(() => {
    if (!tokenMap) return null;
    let totalCodes = 0, totalRaw = 0, totalRemapped = 0, totalShared = 0;
    for (const d of Object.values(tokenMap.reference_domains || (tokenMap as any).domains || {}) as ReferenceDomain[]) {
      totalCodes += d.canonical_values.filter(cv => cv.is_active).length;
      if ((d.used_by_canonical_fields?.length || 0) > 1) totalShared++;
      for (const entries of Object.values(d.crosswalk || {})) {
        for (const e of entries) {
          totalRaw += e.raw_values?.length || 0;
          if (e.sme_remap) totalRemapped++;
        }
      }
    }
    return { totalCodes, totalRaw, totalRemapped, totalShared };
  }, [tokenMap]);

  return (
    <div style={{ padding: '20px 24px', minHeight: '100vh', background: T.bg }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: `linear-gradient(135deg, ${T.brand}, ${T.purple})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FontAwesomeIcon icon={faShareNodes} style={{ fontSize: 17, color: '#fff' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: T.brand }}>Canonical Reference Data</div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
            {canonicalEntityId}
            {tokenMap?.generated_at && <> · Built {new Date(tokenMap.generated_at).toLocaleString()}</>}
            {tokenMap?.version && <> · v{tokenMap.version}</>}
          </div>
        </div>
        <button onClick={handleBuild} disabled={building} className="gw-btn gw-btn-primary">
          <FontAwesomeIcon icon={building ? faRotateRight : faWandMagicSparkles}
            style={{ animation: building ? 'gwspin 1s linear infinite' : 'none' }} />
          {tokenMap ? (building ? 'Rebuilding…' : 'Rebuild') : (building ? 'Building…' : 'Build')}
        </button>
      </div>

      {error && (
        <div className="gw-notice gw-notice-info" style={{ marginBottom: 14 }}>
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </div>
      )}

      {loading && !tokenMap && (
        <div style={{ padding: 48, textAlign: 'center', color: T.textMuted }}>Loading…</div>
      )}

      {/* Stats */}
      {stats && tokenMap && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { val: Object.keys(tokenMap.reference_domains || (tokenMap as any).domains || {}).length, sub: 'reference domains',  color: T.brand  },
            { val: stats.totalCodes,    sub: 'canonical codes',   color: T.green  },
            { val: stats.totalShared,   sub: 'shared by 2+ fields', color: T.purple },
            { val: stats.totalRaw,      sub: 'raw variants',      color: T.amber  },
            { val: stats.totalRaw > 0 ? `${Math.round(stats.totalRaw / Math.max(stats.totalCodes,1))}:1` : '—',
              sub: 'collapse ratio', color: T.teal },
            { val: stats.totalRemapped, sub: 'SME remapped',      color: T.red    },
          ].map(s => (
            <div key={s.sub} style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: 10, padding: '10px 16px', minWidth: 90,
            }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20,
                fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Two-panel */}
      {tokenMap && (
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14, minHeight: 600 }}>

          {/* Sidebar */}
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden',
            background: T.bgCard, alignSelf: 'start' }}>
            <div style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, background: T.bgSidebar }}>
              <input value={sideSearch} onChange={e => setSideSearch(e.target.value)}
                placeholder="Search…" className="gw-input" style={{ fontSize: 11 }} />
            </div>
            <div style={{ maxHeight: 580, overflowY: 'auto' }}>
              {domainList.map((d, i) => (
                <DomainItem key={d.domain_key} domain={d} index={i}
                  active={selectedDomain === d.domain_key}
                  onClick={() => {
                    setSelectedDomain(d.domain_key);
                    setFilterField(null);
                    setActiveTab('values');
                  }} />
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div>
            {activeDomain ? (
              <>
                {/* Domain header */}
                <div style={{
                  padding: '12px 16px', marginBottom: 12,
                  background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10,
                }}>
                  {(() => {
                    const { color, bg, border } = domainColor(activeColorIdx);
                    const cv     = activeDomain.canonical_values;
                    const active = cv.filter(c => c.is_active).length;
                    const raw    = Object.values(activeDomain.crosswalk || {})
                      .flatMap(e => e).reduce((s, e) => s + (e.raw_values?.length || 0), 0);
                    const sources = new Set(
                      Object.values(activeDomain.crosswalk || {}).flatMap(e => e).map((e: any) => e.source_prefix || '')
                    );
                    return (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                          <span style={{
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
                            padding: '3px 10px', borderRadius: 8, background: bg, color, border: `1px solid ${border}`,
                          }}>{activeDomain.domain_key}</span>
                          <div style={{ flex: 1, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: T.textMuted }}>
                              <strong style={{ color }}>{active}</strong> active codes
                            </span>
                            <span style={{ fontSize: 11, color: T.textMuted }}>
                              <strong style={{ color: T.amber }}>{raw}</strong> raw variants
                            </span>
                            <span style={{ fontSize: 11, color: T.textMuted }}>
                              <strong style={{ color: T.brand }}>{sources.size}</strong> source table{sources.size !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {Array.from(sources).map(s => {
                              const ss = sysStyle(s);
                              return (
                                <span key={s} style={{
                                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700,
                                  padding: '2px 7px', borderRadius: 4, background: ss.bg, color: ss.text,
                                }}>{s}</span>
                              );
                            })}
                          </div>
                        </div>
                        {/* Used-by filter strip */}
                        <UsedByStrip
                          usedBy={activeDomain.used_by_canonical_fields || []}
                          filterField={filterField}
                          onFilterField={cf => { setFilterField(cf); if (cf) setActiveTab('crosswalk'); }}
                        />
                      </div>
                    );
                  })()}
                </div>

                {/* Tabs */}
                <div className="gw-tabs" style={{ marginBottom: 14 }}>
                  <button className={cls('gw-tab', activeTab === 'values' && 'active')}
                    onClick={() => setActiveTab('values')}>
                    <FontAwesomeIcon icon={faTags} className="gw-tab-icon" /> Canonical Values
                  </button>
                  <button className={cls('gw-tab', activeTab === 'crosswalk' && 'active')}
                    onClick={() => setActiveTab('crosswalk')}>
                    <FontAwesomeIcon icon={faExchangeAlt} className="gw-tab-icon" /> Source Crosswalk
                    {filterField && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700,
                        padding: '1px 5px', borderRadius: 6,
                        background: T.brandLight, color: T.brand,
                        border: `1px solid ${T.brandMid}`,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>filtered</span>
                    )}
                  </button>
                  <button className={cls('gw-tab', activeTab === 'lineage' && 'active')}
                    onClick={() => setActiveTab('lineage')}>
                    <FontAwesomeIcon icon={faSitemap} className="gw-tab-icon" /> DM Lineage
                  </button>
                </div>

                {activeTab === 'values' && (
                  <CanonicalValuesTab domain={activeDomain} colorIdx={activeColorIdx}
                    canonicalEntityId={canonicalEntityId} onRefresh={load} />
                )}
                {activeTab === 'crosswalk' && (
                  <CrosswalkTab domain={activeDomain} colorIdx={activeColorIdx}
                    canonicalEntityId={canonicalEntityId}
                    filterField={filterField} onRefresh={load} />
                )}
                {activeTab === 'lineage' && (
                  <DmLineageTab domain={activeDomain} colorIdx={activeColorIdx} />
                )}
              </>
            ) : (
              <div style={{ padding: 48, textAlign: 'center', color: T.textMuted,
                background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                Select a reference domain from the left.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CanonicalReferenceData;